import React from 'react'
import { GROUP_COLORS } from './MaterialGroupsPanel'

const partCardStyle = {
  padding: '0.75rem',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  backgroundColor: '#fafafa',
}

const partsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: '0.75rem',
}

export { partsGridStyle }

export default function PartCard({
  part,
  materialGroups = [],
  sheetWidth = 0,
  sheetLength = 0,
  edgeMargin = 0,
  showGroupDropdown = false,
  showColoredBorder = false,
  fillResolved,
  onUpdateField,
  onUpdateQuantity,
  onDelete,
}) {
  // Resolve border styling
  let borderLeft = undefined
  if (showColoredBorder && materialGroups.length > 0) {
    if (part.material_group_id) {
      const idx = materialGroups.findIndex(g => g.group_id === part.material_group_id)
      borderLeft = `3px solid ${GROUP_COLORS[idx % GROUP_COLORS.length] || '#6b7280'}`
    } else {
      borderLeft = '3px dashed #f59e0b'
    }
  }

  // Exceeds sheet check
  const sw = sheetWidth || 0
  const sl = sheetLength || 0
  const em = edgeMargin || 0
  const uw = sw - 2 * em
  const uh = sl - 2 * em
  const pw = part.bounding_width || 0
  const ph = part.bounding_height || 0
  let exceedsSheet = null
  if (sw > 0 && sl > 0 && pw > 0 && ph > 0) {
    const partMin = Math.min(pw, ph), partMax = Math.max(pw, ph)
    const sheetMin = Math.min(uw, uh), sheetMax = Math.max(uw, uh)
    if (partMin > sheetMin || partMax > sheetMax) {
      exceedsSheet = { sheetMax, sheetMin }
    }
  }

  // Group dropdown color
  let dropdownBorderColor = '#f59e0b'
  if (part.material_group_id && materialGroups.length > 0) {
    const idx = materialGroups.findIndex(g => g.group_id === part.material_group_id)
    dropdownBorderColor = GROUP_COLORS[idx % GROUP_COLORS.length] || '#6b7280'
  }

  return (
    <div style={{ ...partCardStyle, ...(borderLeft ? { borderLeft } : {}) }}>
      {part.svg_data && (
        <div style={{
          marginBottom: '0.5rem',
          ...(part.part_rotation ? { transform: `rotate(${part.part_rotation}deg)`, transformOrigin: 'center' } : {}),
        }}
          dangerouslySetInnerHTML={{ __html: part.svg_data }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{part.part_label || part.file_name}</span>
        {showGroupDropdown && (
          <select className="form-select form-select-sm"
            style={{
              width: 'auto', fontSize: '0.72rem', padding: '1px 6px', height: '24px',
              borderColor: dropdownBorderColor,
              color: part.material_group_id ? 'inherit' : '#92400e',
              fontWeight: part.material_group_id ? 400 : 600,
            }}
            value={part.material_group_id || ''}
            onChange={e => onUpdateField(part.part_id, { material_group_id: e.target.value ? parseInt(e.target.value) : null })}>
            <option value="">Assign group...</option>
            {materialGroups.map(g => (
              <option key={g.group_id} value={g.group_id}>
                {g.group_name || `Group ${g.group_id}`}
              </option>
            ))}
          </select>
        )}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
        {part.bounding_width?.toFixed(2)}" x {part.bounding_height?.toFixed(2)}"
      </div>
      {exceedsSheet && (
        <div style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600, marginTop: '0.15rem' }}>
          Exceeds sheet — max usable {exceedsSheet.sheetMax.toFixed(1)}" x {exceedsSheet.sheetMin.toFixed(1)}"
        </div>
      )}
      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
        {part.cut_length_in?.toFixed(1)}" cut | {part.pierce_count} pierces
      </div>
      {part.interior_feature_count > 0 && (
        <div style={{ fontSize: '0.72rem', color: '#9333ea' }}>
          {part.interior_feature_count} interior feature{part.interior_feature_count > 1 ? 's' : ''}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
        <label style={{ fontSize: '0.75rem', margin: 0 }}>Qty:</label>
        <input type="number" min="1"
          value={part.fill_sheet ? '' : part.quantity}
          disabled={!!part.fill_sheet}
          placeholder={part.fill_sheet ? (fillResolved != null ? String(fillResolved) : 'Fill') : ''}
          title={part.fill_sheet
            ? (fillResolved != null
                ? `Last nest placed ${fillResolved} parts (auto-computed)`
                : 'Quantity is auto-computed when Fill Sheet is enabled — run the nest to see the resolved count')
            : 'Quantity'}
          className="form-control form-control-sm"
          style={{
            width: '60px',
            backgroundColor: part.fill_sheet ? '#eff6ff' : undefined,
            color: part.fill_sheet && fillResolved != null ? '#1d4ed8' : undefined,
            fontWeight: part.fill_sheet && fillResolved != null ? 600 : 400,
            fontStyle: part.fill_sheet && fillResolved == null ? 'italic' : 'normal',
            textAlign: part.fill_sheet ? 'center' : undefined,
          }}
          onChange={e => onUpdateQuantity(part.part_id, parseInt(e.target.value) || 1)} />
        <label style={{
          display: 'flex', alignItems: 'center', gap: '0.25rem', margin: 0,
          fontSize: '0.75rem', cursor: 'pointer',
          color: part.fill_sheet ? '#2563eb' : '#6b7280',
          fontWeight: part.fill_sheet ? 600 : 400,
        }}
          title="Fill Sheet: nester computes the maximum qty that fits on one sheet given this part's CL grid. Multiple Fill Sheet entries of the same DXF with different CL grids are jointly optimized.">
          <input type="checkbox" checked={!!part.fill_sheet}
            onChange={e => onUpdateField(part.part_id, { fill_sheet: e.target.checked })} />
          Fill
          {part.fill_sheet && fillResolved != null && (
            <span style={{ color: '#1d4ed8', fontWeight: 600 }}>({fillResolved})</span>
          )}
        </label>
        <button className="btn btn-sm btn-outline-danger" style={{ padding: '2px 8px', fontSize: '0.7rem' }}
          onClick={() => onDelete(part.part_id)}>X</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.35rem', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.75rem', margin: 0 }}>Rot:</label>
        <select className="form-select form-select-sm" style={{ width: '72px', fontSize: '0.75rem', padding: '2px 4px' }}
          value={part.part_rotation ?? ''}
          onChange={e => onUpdateField(part.part_id, { part_rotation: e.target.value === '' ? null : parseInt(e.target.value) })}>
          <option value="">Auto</option>
          <option value="0">0°</option>
          <option value="90">90°</option>
          <option value="180">180°</option>
          <option value="270">270°</option>
        </select>
        <label style={{ fontSize: '0.75rem', margin: 0 }}>Grid:</label>
        <input type="number" min="0" placeholder="C" title="Grid columns (0=auto)"
          value={part.cl_grid_cols || ''}
          className="form-control form-control-sm"
          style={{ width: '42px', fontSize: '0.75rem', padding: '2px 4px' }}
          onChange={e => onUpdateField(part.part_id, { cl_grid_cols: parseInt(e.target.value) || null })} />
        <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>×</span>
        <input type="number" min="0" placeholder="R" title="Grid rows (0=auto)"
          value={part.cl_grid_rows || ''}
          className="form-control form-control-sm"
          style={{ width: '42px', fontSize: '0.75rem', padding: '2px 4px' }}
          onChange={e => onUpdateField(part.part_id, { cl_grid_rows: parseInt(e.target.value) || null })} />
        <label style={{ fontSize: '0.75rem', margin: 0 }} title="Microtabs per part — affects pierce count (2+ tabs = additional pierces)">Tabs:</label>
        <input type="number" min="0" max="20" placeholder="0"
          title="Microtabs per part (0-1 = 1 pierce, 2+ = additional pierces per exterior cut)"
          value={part.microtabs || ''}
          className="form-control form-control-sm"
          style={{ width: '42px', fontSize: '0.75rem', padding: '2px 4px' }}
          onChange={e => onUpdateField(part.part_id, { microtabs: parseInt(e.target.value) || 0 })} />
        <label style={{
          display: 'flex', alignItems: 'center', gap: '0.25rem', margin: 0,
          fontSize: '0.75rem', cursor: 'pointer',
          color: part.common_line ? '#059669' : '#6b7280',
          fontWeight: part.common_line ? 600 : 400,
        }}
          title="Enable common line cutting for this part (parts share cut edges, reducing total cuts)">
          <input type="checkbox" checked={!!part.common_line}
            onChange={e => onUpdateField(part.part_id, { common_line: e.target.checked })} />
          CL
        </label>
      </div>
      {part.warnings?.length > 0 && (
        <div style={{ fontSize: '0.7rem', color: '#d97706', marginTop: '0.25rem' }}>
          {part.warnings.join('; ')}
        </div>
      )}
    </div>
  )
}
